import { useState, useMemo } from 'react';
import { useInventory } from '../../context/InventoryContext';
import { useToast } from '../../context/ToastContext';

// ─── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ item }) {
  if (item.qty === 0) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[var(--wm-text-caption)] font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
        Out of Stock
      </span>
    );
  }
  if (item.qty <= item.reorderAt) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[var(--wm-text-caption)] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
        Low Stock
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[var(--wm-text-caption)] font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
      In Stock
    </span>
  );
}

// ─── Item form modal ──────────────────────────────────────────────────────────
const BLANK_FORM = { sku: '', name: '', brand: '', color: '', unitType: 'roll', qty: '', reorderAt: '', costPerUnit: '', notes: '' };

function ItemModal({ item, onSave, onClose }) {
  const [form, setForm] = useState(item
    ? { sku: item.sku, name: item.name, brand: item.brand, color: item.color, unitType: item.unitType, qty: item.qty, reorderAt: item.reorderAt, costPerUnit: item.costPerUnit, notes: item.notes }
    : { ...BLANK_FORM });

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      qty: Number(form.qty) || 0,
      reorderAt: Number(form.reorderAt) || 1,
      costPerUnit: Number(form.costPerUnit) || 0,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-[#1B2A3E] rounded-xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-[#243348]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-[#243348]">
          <h2 className="text-[var(--wm-text-heading)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">
            {item ? 'Edit Item' : 'Add Inventory Item'}
          </h2>
          <button onClick={onClose} className="text-[#64748B] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] mb-1">SKU</label>
              <input value={form.sku} onChange={e => set('sku', e.target.value)} className="wm-input w-full" placeholder="3M-1080-MB" />
            </div>
            <div>
              <label className="block text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] mb-1">Unit Type</label>
              <select value={form.unitType} onChange={e => set('unitType', e.target.value)} className="wm-input w-full">
                {['roll', 'sqft', 'kit', 'each', 'gallon', 'bottle', 'case'].map(u => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] mb-1">Name *</label>
            <input required value={form.name} onChange={e => set('name', e.target.value)} className="wm-input w-full" placeholder="3M 1080 Series Matte Black" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] mb-1">Brand</label>
              <input value={form.brand} onChange={e => set('brand', e.target.value)} className="wm-input w-full" placeholder="3M" />
            </div>
            <div>
              <label className="block text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] mb-1">Color / Variant</label>
              <input value={form.color} onChange={e => set('color', e.target.value)} className="wm-input w-full" placeholder="Matte Black" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] mb-1">Qty</label>
              <input type="number" min="0" value={form.qty} onChange={e => set('qty', e.target.value)} className="wm-input w-full" placeholder="0" />
            </div>
            <div>
              <label className="block text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] mb-1">Reorder At</label>
              <input type="number" min="0" value={form.reorderAt} onChange={e => set('reorderAt', e.target.value)} className="wm-input w-full" placeholder="1" />
            </div>
            <div>
              <label className="block text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] mb-1">Cost / Unit ($)</label>
              <input type="number" min="0" step="0.01" value={form.costPerUnit} onChange={e => set('costPerUnit', e.target.value)} className="wm-input w-full" placeholder="180" />
            </div>
          </div>
          <div>
            <label className="block text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} className="wm-input w-full resize-none" placeholder="Optional notes..." />
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-[var(--wm-text-ui)] font-medium rounded-lg border border-gray-200 dark:border-[#243348] text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-50 dark:hover:bg-[#243348] transition-colors">
              Cancel
            </button>
            <button type="submit" className="wm-btn-primary">
              {item ? 'Save Changes' : 'Add Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Adjust Qty modal ─────────────────────────────────────────────────────────
function AdjustModal({ item, onSave, onClose }) {
  const [delta, setDelta] = useState('');
  const [note, setNote] = useState('');
  const [direction, setDirection] = useState('add');

  const handleSubmit = (e) => {
    e.preventDefault();
    const numDelta = Number(delta) || 0;
    onSave(direction === 'add' ? numDelta : -numDelta, note);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-[#1B2A3E] rounded-xl shadow-2xl w-full max-w-sm border border-gray-200 dark:border-[#243348]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-[#243348]">
          <h2 className="text-[var(--wm-text-heading)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">Adjust Qty — {item.name}</h2>
          <button onClick={onClose} className="text-[#64748B] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3">
          <p className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE]">Current qty: <span className="font-semibold text-[#0F1923] dark:text-[#F8FAFE]">{item.qty} {item.unitType}</span></p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setDirection('add')}
              className={`flex-1 py-2 text-[var(--wm-text-ui)] font-semibold rounded-lg border transition-colors ${direction === 'add' ? 'bg-emerald-50 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-400' : 'border-gray-200 dark:border-[#243348] text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-50 dark:hover:bg-[#243348]'}`}
            >
              + Add
            </button>
            <button
              type="button"
              onClick={() => setDirection('remove')}
              className={`flex-1 py-2 text-[var(--wm-text-ui)] font-semibold rounded-lg border transition-colors ${direction === 'remove' ? 'bg-red-50 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-400' : 'border-gray-200 dark:border-[#243348] text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-50 dark:hover:bg-[#243348]'}`}
            >
              - Remove
            </button>
          </div>
          <div>
            <label className="block text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] mb-1">Amount</label>
            <input
              required
              type="number"
              min="1"
              value={delta}
              onChange={e => setDelta(e.target.value)}
              className="wm-input w-full"
              placeholder="1"
            />
          </div>
          <div>
            <label className="block text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] mb-1">Note (optional)</label>
            <input value={note} onChange={e => setNote(e.target.value)} className="wm-input w-full" placeholder="Used on job #WM-0042" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-[var(--wm-text-ui)] font-medium rounded-lg border border-gray-200 dark:border-[#243348] text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-50 dark:hover:bg-[#243348] transition-colors">Cancel</button>
            <button type="submit" className="wm-btn-primary">Apply</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function InventoryPage() {
  const { inventory, lowStockCount, addItem, updateItem, deleteItem, adjustQty } = useInventory();
  const { addToast } = useToast();

  const [search, setSearch] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [editItem, setEditItem] = useState(null);     // item object or null
  const [showAdd, setShowAdd] = useState(false);
  const [adjustItem, setAdjustItem] = useState(null); // item object for qty adjust
  const [deleteConfirm, setDeleteConfirm] = useState(null); // item id

  const filtered = useMemo(() => {
    let items = inventory;
    if (lowStockOnly) items = items.filter(i => i.qty <= i.reorderAt);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(i =>
        i.name.toLowerCase().includes(q) ||
        i.brand.toLowerCase().includes(q) ||
        i.sku.toLowerCase().includes(q)
      );
    }
    return items;
  }, [inventory, lowStockOnly, search]);

  const exportCSV = () => {
    const headers = ['SKU', 'Name', 'Brand', 'Color', 'Unit Type', 'Qty', 'Reorder At', 'Cost/Unit', 'Status', 'Notes'];
    const rows = inventory.map(i => {
      const status = i.qty === 0 ? 'Out of Stock' : i.qty <= i.reorderAt ? 'Low Stock' : 'In Stock';
      return [i.sku, i.name, i.brand, i.color, i.unitType, i.qty, i.reorderAt, i.costPerUnit, status, `"${(i.notes || '').replace(/"/g, '""')}"`];
    });
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Page header */}
      <div className="h-10 bg-white dark:bg-[#1B2A3E] border-b border-gray-200 dark:border-[#243348] flex items-center px-5 flex-shrink-0 gap-3">
        <span className="text-[var(--wm-text-heading)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">Inventory</span>
        {lowStockCount > 0 && (
          <span className="px-1.5 py-0.5 text-[var(--wm-text-caption)] font-bold rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
            {lowStockCount} low
          </span>
        )}
        <div className="flex-1" />
        <button
          onClick={exportCSV}
          className="flex items-center gap-1.5 px-2.5 py-1 text-[var(--wm-text-ui)] font-medium rounded-lg border border-gray-200 dark:border-[#243348] text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-50 dark:hover:bg-[#243348] transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
          </svg>
          Export CSV
        </button>
        <button
          onClick={() => setShowAdd(true)}
          className="wm-btn-primary"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          Add Item
        </button>
      </div>

      {/* Toolbar */}
      <div className="px-5 py-3 bg-white dark:bg-[#1B2A3E] border-b border-gray-200 dark:border-[#243348] flex items-center gap-3 flex-shrink-0">
        <div className="relative flex-1 max-w-xs">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#64748B] dark:text-[#7D93AE] pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, brand or SKU..."
            className="wm-input w-full pl-8 text-[var(--wm-text-ui)]"
          />
        </div>
        <label className="flex items-center gap-2 text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] cursor-pointer select-none">
          <input
            type="checkbox"
            checked={lowStockOnly}
            onChange={e => setLowStockOnly(e.target.checked)}
            className="rounded border-gray-300 dark:border-[#243348] text-amber-500 focus:ring-amber-400"
          />
          Low stock only
        </label>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full border-collapse min-w-[700px]">
          <thead className="sticky top-0 z-10">
            <tr className="bg-gray-50 dark:bg-[#0F1923] border-b border-gray-200 dark:border-[#243348]">
              <th className="text-left px-5 py-2.5 text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] whitespace-nowrap">Name</th>
              <th className="text-left px-4 py-2.5 text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] whitespace-nowrap">Brand</th>
              <th className="text-left px-4 py-2.5 text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] whitespace-nowrap">Type</th>
              <th className="text-right px-4 py-2.5 text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] whitespace-nowrap">Qty</th>
              <th className="text-right px-4 py-2.5 text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] whitespace-nowrap">Reorder At</th>
              <th className="text-right px-4 py-2.5 text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] whitespace-nowrap">Cost/Unit</th>
              <th className="text-left px-4 py-2.5 text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] whitespace-nowrap">Status</th>
              <th className="text-right px-4 py-2.5 text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-[#243348]">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-12 text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">
                  {search || lowStockOnly ? 'No items match your filters.' : 'No inventory items yet. Click "Add Item" to get started.'}
                </td>
              </tr>
            )}
            {filtered.map(item => {
              const isLow = item.qty > 0 && item.qty <= item.reorderAt;
              const isOut = item.qty === 0;
              return (
                <tr
                  key={item.id}
                  className={`group transition-colors ${isOut ? 'bg-red-50/40 dark:bg-red-900/10' : isLow ? 'bg-amber-50/50 dark:bg-amber-900/10' : 'hover:bg-gray-50 dark:hover:bg-[#1B2A3E]/50'}`}
                >
                  <td className="px-5 py-3 text-[var(--wm-text-body)]">
                    <div className="font-medium text-[#0F1923] dark:text-[#F8FAFE]">{item.name}</div>
                    {item.sku && <div className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE] font-mono mt-0.5">{item.sku}</div>}
                  </td>
                  <td className="px-4 py-3 text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">{item.brand || '—'}</td>
                  <td className="px-4 py-3 text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE] capitalize">{item.unitType}</td>
                  <td className="px-4 py-3 text-right text-[var(--wm-text-body)] font-semibold tabular-nums text-[#0F1923] dark:text-[#F8FAFE]">{item.qty}</td>
                  <td className="px-4 py-3 text-right text-[var(--wm-text-body)] tabular-nums text-[#64748B] dark:text-[#7D93AE]">{item.reorderAt}</td>
                  <td className="px-4 py-3 text-right text-[var(--wm-text-body)] tabular-nums text-[#64748B] dark:text-[#7D93AE]">${item.costPerUnit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="px-4 py-3"><StatusBadge item={item} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {/* Adjust Qty */}
                      <button
                        onClick={() => setAdjustItem(item)}
                        title="Adjust Qty"
                        className="p-1.5 rounded-lg text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-100 dark:hover:bg-[#243348] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                      </button>
                      {/* Edit */}
                      <button
                        onClick={() => setEditItem(item)}
                        title="Edit"
                        className="p-1.5 rounded-lg text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-100 dark:hover:bg-[#243348] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" />
                        </svg>
                      </button>
                      {/* Delete */}
                      <button
                        onClick={() => setDeleteConfirm(item.id)}
                        title="Delete"
                        className="p-1.5 rounded-lg text-[#64748B] dark:text-[#7D93AE] hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add modal */}
      {showAdd && (
        <ItemModal
          item={null}
          onSave={(data) => { addItem(data); addToast('Item saved.', 'success'); setShowAdd(false); }}
          onClose={() => setShowAdd(false)}
        />
      )}

      {/* Edit modal */}
      {editItem && (
        <ItemModal
          item={editItem}
          onSave={(data) => { updateItem(editItem.id, data); addToast('Item saved.', 'success'); setEditItem(null); }}
          onClose={() => setEditItem(null)}
        />
      )}

      {/* Adjust Qty modal */}
      {adjustItem && (
        <AdjustModal
          item={adjustItem}
          onSave={(delta, note) => { adjustQty(adjustItem.id, delta, note); setAdjustItem(null); }}
          onClose={() => setAdjustItem(null)}
        />
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-[#1B2A3E] rounded-xl shadow-2xl w-full max-w-sm border border-gray-200 dark:border-[#243348] p-5">
            <h2 className="text-[var(--wm-text-heading)] font-semibold text-[#0F1923] dark:text-[#F8FAFE] mb-2">Delete Item?</h2>
            <p className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE] mb-4">This action cannot be undone.</p>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setDeleteConfirm(null)} className="px-3 py-1.5 text-[var(--wm-text-ui)] font-medium rounded-lg border border-gray-200 dark:border-[#243348] text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-50 dark:hover:bg-[#243348] transition-colors">Cancel</button>
              <button onClick={() => { deleteItem(deleteConfirm); setDeleteConfirm(null); }} className="px-3 py-1.5 text-[var(--wm-text-ui)] font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
